/**
 * Rechte der Etiketten an EINER Stelle (Story 27.2, AD-42, [RUDOLF 02.11.2026]); seit Story 28.4 aus dem Rechte-Katalog.
 *
 * - `design`: Vorlagen anlegen, speichern, duplizieren, archivieren, Fassung wiederherstellen, Bild hochladen, Vorlagen
 *   und Felder lesen = Recht `labels:write` (Startwerte: Admin, Büro, Buchhaltung, Packplatz; nicht Mitarbeiter).
 * - `admin`: Druckerprofile und Standard-Vorlage je Anlass festlegen, Testdruck = `settings:edit` (Startwert nur Admin).
 * - `print`: Etiketten drucken (Story 27.6) = Recht `labels:read` (Startwerte: Admin, Büro, Buchhaltung, Packplatz; nicht
 *   Mitarbeiter – wie die Rollenliste aus 27.6).
 * - Actor nur `user`: die KI (`ai`), MCP und Regeln legen keine Vorlagen an.
 * - Felder: ohne `prices:sales` keine Geld-Spalten (Typ `money`) im Feld-Katalog (heute: Packplatz).
 *
 * Commands, Routen und die Web-App fragen nur hier. Browser-sicher (Story 27.5): nur Typen aus `platform` und der reine
 * Rechte-Kern `@till-erp/platform/permissions`, über `@till-erp/documents/values` auch in `apps/web`; das 403
 * (`assertLabelRight`) steht in `access-guard.ts`.
 */
import type { ActorType, EffectivePermissions, PermissionKey, Role } from '@till-erp/platform';
import { can, legacyRolePermissions } from '@till-erp/platform/permissions';

export type LabelRight = 'design' | 'admin' | 'print';

export const LABEL_RIGHTS: Readonly<Record<LabelRight, PermissionKey>> = {
  design: 'labels:write',
  admin: 'settings:edit',
  print: 'labels:read',
};

/** Actor-Typen, die Etiketten-Commands auslösen dürfen. */
export const LABEL_ACTORS = ['user'] as const satisfies readonly ActorType[];

/** Haben die Rechte der Person dieses Recht? Ohne Rechte (keine Person) nie. */
export function hasLabelPermission(permissions: EffectivePermissions | null | undefined, right: LabelRight): boolean {
  return permissions != null && can(permissions, LABEL_RIGHTS[right]);
}

/**
 * Darf die alte Rolle das Recht ausüben? (Startwerte der Grundrolle) – für die Web-App, bis das Menü mit 28.10 die Rechte
 * aus `GET /me` nimmt. Server und Commands prüfen mit `hasLabelPermission`.
 */
export function hasLabelRight(role: Role | null | undefined, right: LabelRight): boolean {
  return hasLabelPermission(role == null ? null : legacyRolePermissions(role), right);
}

/** Darf die Person dieses Feld (Spaltentyp) im Feld-Katalog sehen? Geld-Spalten nur mit `prices:sales`. */
export function labelFieldVisible(permissions: EffectivePermissions | null | undefined, column: { type: string }): boolean {
  if (!hasLabelPermission(permissions, 'design')) return false;
  if (column.type === 'money' && !can(permissions!, 'prices:sales')) return false;
  return true;
}
